import type React from "react"
import { memo, useMemo, useState } from "react"
import { animated, config, useSpring } from "@react-spring/web"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import UtilitySideMenuButton, { UtilitySideMenuButtonProps } from "./UtilitySideMenuButton";
import styles from "../SideMenu.module.scss";


import { MenuItemData, SubMenuData } from "../menu.types";
import { MENU_STRUCTURE } from "./UtilityMenu.constant";
import { ApplicationRoutes } from "../../routes"
import { logOut } from "../../stores/slices/current-user-slice"
import { CloseMobileMenuIcon, LogOutIcon } from "../../assets/icons"
import { hasPermission } from "../../guards/common/authentification-functions"
import { useLogoutMutation } from "../../service/authentication-service"
import { useTenant } from "../../tenant/branding/useTenant"
import { TenantLogo } from "../../tenant/branding/TenantLogo"

type Props = {
  isColapsed: boolean
  isDisabled: boolean
  mobileMenuOpen: boolean
  onMobileMenuClick: () => void
  onSubMenuInteraction: (mainMenuName: string, subMenuItems: SubMenuData[], isOpen: boolean, activeSubMenuItem?: string) => void
}

export const createSideMenuButtons = (
  currentPath: string,
  colapsed: boolean,
  isDisabled: boolean,
  executeWrapper?: (func: () => void) => void,
  onSubMenuInteraction?: (mainMenuName: string, subMenuItems: SubMenuData[], isOpen: boolean, activeSubMenuItem?: string) => void
): UtilitySideMenuButtonProps[] => {

  let menuItems: MenuItemData[] = [];

  // Detect module from URL and load appropriate menu
  switch (true) {
    case currentPath.includes(`${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.BILLS_OPERATIONS}`):
      menuItems = MENU_STRUCTURE.billsOperationsMenu;
      break;
    case currentPath.includes(`${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.SERVICE_REQUEST_MODULE}`):
      menuItems = MENU_STRUCTURE.serviceRequestMenu;
      break;
    case currentPath.includes(`${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.CUSTOMER_SUPPORT}`):
      menuItems = MENU_STRUCTURE.customerSupportMenu;
      break;
    case currentPath.includes(`${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.CLIENT_MANAGEMENT}`):
      menuItems = MENU_STRUCTURE.clientManagementMenu;
      break;
    default:
      return [];
  }


  return menuItems.filter(item => item.permission ? hasPermission(item.permission) : true).map((item) => ({
    text: item.text,
    icon: item.icon,
    isActiveIcon: item.isActiveIcon,
    route: item.route,
    disabled: item.disabled,
    executeWrapper,
    colapsed,
    isDisabled,
    subMenuItems: item.subMenuItems,
    onSubMenuInteraction,
    activeOnRoutes: item.activeOnRoutes,
  }));
};

const UtilitySideMenu: React.FC<Props> = memo((props: Props) => {
  const { isColapsed, isDisabled, mobileMenuOpen, onMobileMenuClick, onSubMenuInteraction } = props

  const location = useLocation()
  const dispatch = useDispatch()
  const navigator = useNavigate()
  const currentPath = location.pathname

  const [currentOpenSubmenu, setCurrentOpenSubmenu] = useState<string | null>(null);

  const [logoutApi] = useLogoutMutation();
  const { logo } = useTenant();

  const logout = async () => {
    await logoutApi();
    dispatch(logOut())
    navigator(`/${ApplicationRoutes.ADMIN_UTIL_SECRET}`)
  }

  const handleSubMenuInteraction = (mainMenuName: string, subMenuItems: SubMenuData[], isOpen: boolean, activeSubMenuItem?: string) => {
    setCurrentOpenSubmenu(isOpen ? mainMenuName : null);

    if (onSubMenuInteraction) {
      onSubMenuInteraction(mainMenuName, subMenuItems, isOpen, activeSubMenuItem);
    }
  };

  const menuStyles = useSpring({
    from: {
      transform: mobileMenuOpen ? "translateX(-100%)" : "translateX(0%)",
    },
    to: {
      transform: mobileMenuOpen ? "translateX(0%)" : "translateX(-100%)",
    },
    config: config.gentle,
  })

  const executeWrapper = useMemo(
    () => (func: () => void) => {
      func()
      onMobileMenuClick()
    },
    [onMobileMenuClick],
  )

  const menuButtons = useMemo(() => {
    return createSideMenuButtons(
      currentPath,
      isColapsed,
      isDisabled,
      executeWrapper,
      handleSubMenuInteraction
    ).map(buttonProps => ({
      ...buttonProps,
      currentOpenSubmenu
    }));
  }, [currentPath, isColapsed, isDisabled, executeWrapper, currentOpenSubmenu]);

  const renderButtons = useMemo(() => {

    return menuButtons.map((button, index) => {
      const buttonWithWrapper = {
        ...button,
        executeWrapper,
      }

      return <UtilitySideMenuButton onSubMenuInteraction={onSubMenuInteraction} key={button.text} {...buttonWithWrapper} />
    })
  }, [menuButtons])

  return (
    <>
      <div className={`${styles.container}`}>
        <div className={`${styles.logoContainer}`}>
          <a href={`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.ADMIN_LANDING}`}>
            <TenantLogo logo={logo} alt="Logo" className={styles.logo} />
          </a>
        </div>
        {renderButtons}
        <button title="Logout" className={`standardButton ${styles.logoutButton} ${styles.colapsed}`} onClick={logout}>
          <LogOutIcon />
        </button>
      </div>
      <animated.div style={menuStyles} className={styles.mobileMenuContainer}>
        <div className={styles.innerContainer}>
          <div className={styles.topbar}>
            <TenantLogo logo={logo} alt="Logo" className={styles.logo} style={{ cursor: "pointer" }} onClick={() => { navigator(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.ADMIN_LANDING}`); onMobileMenuClick(); }} />
            <CloseMobileMenuIcon onClick={onMobileMenuClick} style={{ color: "#42577A" }} />
          </div>
          <div className={styles.buttonSection}>{renderButtons}</div>
          <div className={styles.logoutContainer}>
            <button className={styles.mobileLogout} onClick={logout}>
              <LogOutIcon />
              <span className={styles.text}>Logout</span>
            </button>
          </div>
        </div>
      </animated.div>
    </>
  )
})

export default UtilitySideMenu
